"use client";
import { IResource } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { Search, X } from "lucide-react";
import React from "react";
import { BuilderResourceEditor } from "@/features/map/components/builder-resource-editor/BuilderResourceEditor";
import { ResourceSettingsProvider } from "@/features/reserve/hooks/useResourceSettings";

export interface MapBuilderResourcesPanelProps {
  /** Floor whose resources are searchable. */
  floorId: string;
  /** All office resources (floor filtering happens here). */
  resources: IResource[] | undefined;
  /** Resource ids currently selected for editing (canvas selection or
   *  search picks), in selection order. */
  selectedResourceIds: string[];
  onAddResource: (id: string) => void;
  onRemoveResource: (id: string) => void;
  onClear: () => void;
  onClose: () => void;
  /** Called with the resources just saved (bulk or single) so the caller
   *  can mirror the new name/label onto the matching canvas spot shapes —
   *  a resource edit here otherwise never reaches the map's shape data. */
  onResourcesSaved?: (
    updates: { id: string; name: string; label?: string | null }[]
  ) => void;
}

/**
 * Resource editor for the map builder — replaces the legacy MapEditPanel
 * slide-over (which rendered outside the editor's body portal and ended up
 * invisible underneath it). Renders as an integrated right-side column of
 * the full-screen editor: select spots on the canvas (selection syncs in)
 * or search the floor's resources, then edit them with the standard
 * ResourcePanel form.
 */
export const MapBuilderResourcesPanel: React.FC<
  MapBuilderResourcesPanelProps
> = ({
  floorId,
  resources,
  selectedResourceIds,
  onAddResource,
  onRemoveResource,
  onClear,
  onClose,
  onResourcesSaved
}) => {
  const [query, setQuery] = React.useState("");

  const floorResources = React.useMemo(
    () => (resources || []).filter((r) => r.parent_id === floorId),
    [resources, floorId]
  );

  const searchResults = React.useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return [];
    return floorResources
      .filter((r) => r.name.toLowerCase().includes(trimmed))
      .slice(0, 30);
  }, [floorResources, query]);

  // Resolve ids → resources preserving the selection order (the bulk-edit
  // form numbers entries in the order the user picked them).
  const selectedResources = React.useMemo(() => {
    const byId = new Map((resources || []).map((r) => [r.id, r]));
    return selectedResourceIds
      .map((id) => byId.get(id))
      .filter((r): r is IResource => !!r);
  }, [resources, selectedResourceIds]);

  return (
    <ResourceSettingsProvider>
      <div className="flex h-full w-[400px] flex-none flex-col border-neutral-200 border-l bg-white">
        {/* Header */}
        <div className="flex h-12 flex-none items-center justify-between border-neutral-200 border-b px-4">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-neutral-900 text-sm">
              Edit Resources
            </span>
            {selectedResources.length > 0 && (
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 font-medium text-[11px] text-neutral-600">
                {selectedResources.length}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
            title="Close resources editor"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="flex-none border-neutral-200 border-b px-4 py-3">
          <div className="relative">
            <Search
              size={14}
              className="-translate-y-1/2 absolute top-1/2 left-2.5 text-neutral-400"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search resources on this floor…"
              className="h-8 w-full rounded-md border border-neutral-200 bg-neutral-50 pr-7 pl-8 text-neutral-800 text-sm placeholder-neutral-400 focus:border-neutral-300 focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="-translate-y-1/2 absolute top-1/2 right-2 text-neutral-400 hover:text-neutral-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {query.trim() && (
            <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-neutral-200">
              {searchResults.length === 0 ? (
                <p className="px-3 py-3 text-neutral-400 text-xs">
                  No resources match this search.
                </p>
              ) : (
                searchResults.map((resource) => {
                  const isSelected = selectedResourceIds.includes(resource.id);
                  return (
                    <button
                      key={resource.id}
                      type="button"
                      onClick={() =>
                        isSelected
                          ? onRemoveResource(resource.id)
                          : onAddResource(resource.id)
                      }
                      className={cn(
                        "flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-neutral-50",
                        isSelected ? "text-neutral-400" : "text-neutral-800"
                      )}
                    >
                      <span className="truncate">{resource.name}</span>
                      {isSelected && (
                        <span className="ml-2 flex-none rounded-full bg-green-100 px-2 py-0.5 text-[11px] text-green-700">
                          Selected
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {selectedResources.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
              <p className="font-medium text-neutral-700 text-sm">
                Nothing selected yet
              </p>
              <p className="text-neutral-400 text-xs">
                Click spots on the map or search above to edit their resources.
                Select several to bulk edit.
              </p>
            </div>
          ) : (
            <div className="flex h-full flex-col gap-3">
              <div className="flex flex-none flex-wrap items-center gap-1.5">
                {selectedResources.map((resource) => (
                  <span
                    key={resource.id}
                    className="inline-flex items-center gap-1 rounded-full bg-neutral-100 py-0.5 pr-1 pl-2.5 text-neutral-700 text-xs"
                  >
                    <span className="max-w-36 truncate">{resource.name}</span>
                    <button
                      type="button"
                      onClick={() => onRemoveResource(resource.id)}
                      className="rounded-full p-0.5 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700"
                      title="Remove from selection"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={onClear}
                  className="ml-1 text-neutral-400 text-xs underline hover:text-neutral-600"
                >
                  Clear all
                </button>
              </div>
              <BuilderResourceEditor
                resources={selectedResources}
                onResourcesSaved={onResourcesSaved}
              />
            </div>
          )}
        </div>
      </div>
    </ResourceSettingsProvider>
  );
};
